/* =========================================================================
   Subscriptions3 — the /subscriptions page, restyled to match LandingPage6's
   "From Idea to App" Grand Slam Offer (navy / cyan / purple lp6 theme). It
   reuses the same lp6.* copy and .lp6 styles so the landing → checkout handoff
   looks seamless. The existing Stripe checkout flow (navigateCheckout) is
   preserved.

   NOTE: the displayed founding price ($497) is the OFFER. The button calls
   requestCourseCheckout — the one-time (mode: "payment") $497 "CD Course"
   purchase, Stripe price price_1TopUaH68hrb2WhFDSpJJhfG. On success the
   webhook sets courseAccess: true, which RestrictedRoute honors to unlock
   the course.

   Sales are US-only and adults-only: the CTA stays disabled until the 18+ / US
   box is ticked, and the backend refuses a checkout without it
   (CHECKOUT_ELIGIBILITY in config/legal.js), or when the profile says the
   person lives in Mexico or Costa Rica (course_country_not_sold). Visitors
   who are not signed in tick it again on /register-to-plan.

   While ENROLLMENT_OPEN is false (production, until the course is finished)
   the offer still shows but the CTA becomes an inert "coming soon" badge —
   no one reaches Stripe. See src/config/enrollment.js.
   ========================================================================= */

import React, { useEffect, useState } from "react";

import { requestCourseCheckout } from "../../services/userService";
import { ENROLLMENT_OPEN } from "../../config/enrollment";
import { CHECKOUT_ELIGIBILITY, LEGAL_PATHS } from "../../config/legal";
import { isStandardUserRole } from "../../utils/roles";

// reactstrap components
import { Container, Row, Col } from "reactstrap";

import { useNavigate, useSearchParams } from "react-router-dom";
import { useTranslation } from "react-i18next";

// icons
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faShieldHalved,
  faUsers,
  faClock,
} from "@fortawesome/free-solid-svg-icons";

// core components
import DemoNavbar from "components/Navbars/DemoNavbar.js";
import MainFooter from "components/Footers/MainFooter.js";
import ValueStack, { SHOW_STACK_PRICES } from "components/Offer/ValueStack.js";
import { LegalCheckbox, LegalLinks } from "components/Legal/LegalConsent";
import { useUserAuth } from "../../context/UserContext";
import useReveal from "hooks/useReveal";

// Reuse the landing page styles so the offer looks identical end-to-end.
import "./LandingPage6.css";

// Backend refusals with their own message. Anything else says checkout
// could not start.
const COURSE_ERROR_KEYS = {
  eligibility_required: "legal.eligibilityRequired",
  // The profile says the person lives in Mexico or Costa Rica.
  course_country_not_sold: "legal.courseCountryNotSold",
};

const Subscriptions3 = () => {
  const navigate = useNavigate();
  const { user } = useUserAuth();
  const { t, i18n } = useTranslation();
  // The 18+ / US box, never pre-ticked; `starting` while the checkout is
  // being created; `checkoutError` is shown under the button.
  const [eligible, setEligible] = useState(false);
  const [starting, setStarting] = useState(false);
  const [checkoutError, setCheckoutError] = useState(null);

  // Where to return the user after payment. Set by RestrictedRoute when it
  // bounces someone off a paywalled lesson (?next=/CD_M1_L3); empty otherwise.
  const [searchParams] = useSearchParams();
  const nextPath = searchParams.get("next") || "";

  // Fade-and-rise for the [data-reveal] blocks below (styles/premium.css).
  useReveal();

  // Match the landing page's floating navbar (transparent over the navy header,
  // solid on scroll). Cleaned up on unmount so other pages keep the solid one.
  useEffect(() => {
    document.body.classList.add("lp6-active");
    const onScroll = () => {
      document.body.classList.toggle("lp6-nav-solid", window.scrollY > 40);
    };
    onScroll();
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => {
      document.body.classList.remove("lp6-active", "lp6-nav-solid");
      window.removeEventListener("scroll", onScroll);
    };
  }, []);

  // `starting` stays true while the browser leaves for Stripe. Back from Stripe
  // can restore this page from the back/forward cache (Safari), so clear it
  // there or the box and the button stay disabled until a reload.
  useEffect(() => {
    const onShow = (event) => {
      if (event.persisted) setStarting(false);
    };
    window.addEventListener("pageshow", onShow);
    return () => window.removeEventListener("pageshow", onShow);
  }, []);

  // The founding seat is the one-time $497 "CD Course" purchase (Stripe price
  // price_1TopUaH68hrb2WhFDSpJJhfG). Logged-in users go straight to that Stripe
  // Checkout; everyone else registers first (RegisterToPlan finishes the same
  // one-time checkout after sign-up).
  const navigateCheckout = async () => {
    // Belt and braces: the button is replaced by a badge when enrollment is
    // closed, so this should be unreachable — but never start a checkout the
    // course isn't ready to honor.
    if (!ENROLLMENT_OPEN) return;
    if (!eligible) return;

    if (user && isStandardUserRole(user.role)) {
      setStarting(true);
      setCheckoutError(null);
      const locale = (i18n.language || "es").startsWith("en") ? "en" : "es";
      try {
        const responseURL = await requestCourseCheckout(user.uid, user.email, nextPath, {
          eligibility: CHECKOUT_ELIGIBILITY,
          locale,
        });
        // Guard so we never set window.location.href = undefined (which
        // navigates to /undefined and the SPA silently bounces home).
        if (responseURL) {
          window.location.href = responseURL;
          return;
        }
        console.error("Course checkout failed — no redirect URL returned.");
        setCheckoutError(t("legal.checkoutError"));
      } catch (error) {
        setCheckoutError(t(COURSE_ERROR_KEYS[error?.code] || "legal.checkoutError"));
      }
      setStarting(false);
    } else {
      // Carry the destination through the sign-up step so it survives to checkout.
      navigate(nextPath ? `/register-to-plan?next=${encodeURIComponent(nextPath)}` : "/register-to-plan");
    }
  };

  return (
    <>
      <DemoNavbar />
      <main className="lp6">
        {/* ----------------------------- HEADER --------------------------- */}
        <section className="lp6-offer-hero">
          <span className="lp6-orb lp6-orb-1" aria-hidden="true" />
          <span className="lp6-orb lp6-orb-2" aria-hidden="true" />
          <span className="lp6-orb lp6-orb-3" aria-hidden="true" />
          <span className="cn-grain" aria-hidden="true" />
          <Container>
            <div className="lp6-section-head lp6-head-dark">
              {/* lp6-eyebrow-soon only adds a slow "in progress" pulse to the
                  eyebrow's dot while enrollment is closed. */}
              <span
                className={`lp6-price-eyebrow${ENROLLMENT_OPEN ? "" : " lp6-eyebrow-soon"}`}
                data-reveal=""
              >
                {ENROLLMENT_OPEN
                  ? t("lp6.priceEyebrow")
                  : t("enrollment.comingSoonEyebrow")}
              </span>
              {/* The page's one h1. */}
              <h1 data-reveal="" style={{ "--reveal-delay": "70ms" }}>
                {ENROLLMENT_OPEN
                  ? t("lp6.priceTitle")
                  : t("enrollment.comingSoonTitle")}
              </h1>
              <p data-reveal="" style={{ "--reveal-delay": "140ms" }}>
                {ENROLLMENT_OPEN
                  ? t("lp6.priceSubtitle")
                  : t("enrollment.comingSoonBody")}
              </p>
            </div>
          </Container>
        </section>

        {/* ---------------------- OFFER / WHAT'S INCLUDED ------------------ */}
        <section className="lp6-offer">
          <Container>
            <Row className="lp6-offer-grid">
              <ValueStack prefix="lp6" reveal />

              <Col lg="5">
                <div
                  className="lp6-price-card"
                  data-reveal=""
                  style={{ "--reveal-delay": "90ms" }}
                >
                  <span className="lp6-price-eyebrow">
                    {t("lp6.priceEyebrow")}
                  </span>
                  <h2 className="lp6-price-title">{t("lp6.priceTitle")}</h2>
                  <p className="lp6-price-sub">{t("lp6.priceSubtitle")}</p>

                  <div className="lp6-price-amount">
                    {/* The struck-through price is the value-stack total, so it
                        only makes sense while the stack is on the page. With the
                        stack shelved, the comparison line below carries the
                        anchor instead. */}
                    {SHOW_STACK_PRICES && (
                      <span className="lp6-price-strike">
                        {t("lp6.stackTotalValue")}
                      </span>
                    )}
                    <span className="lp6-price-now">{t("lp6.priceAmount")}</span>
                  </div>
                  <span className="lp6-price-plan">{t("lp6.pricePlan")}</span>
                  {!SHOW_STACK_PRICES && (
                    <span className="lp6-price-compare">
                      {t("lp6.priceCompare")}
                    </span>
                  )}

                  {ENROLLMENT_OPEN ? (
                    <>
                      <div style={{ marginTop: 24 }}>
                        <LegalCheckbox
                          id="course-eligible"
                          checked={eligible}
                          onChange={(e) => setEligible(e.target.checked)}
                          disabled={starting}
                          describedBy="course-eligible-hint"
                        >
                          {t("legal.eligibilityConfirm")}
                        </LegalCheckbox>
                        <p className="cn-legal-links" id="course-eligible-hint">
                          {t("legal.usOnlyNote")}
                        </p>
                      </div>

                      {/* Disabled until the 18+ / US box is ticked. */}
                      <button
                        className="lp6-btn-primary lp6-price-btn"
                        onClick={navigateCheckout}
                        disabled={!eligible || starting}
                        aria-describedby={!eligible ? "course-eligible-required" : undefined}
                      >
                        {t("lp6.priceCta")}
                      </button>
                      {!eligible && (
                        <p className="cn-legal-links" id="course-eligible-required">
                          {t("legal.eligibilityRequired")}
                        </p>
                      )}
                      {checkoutError && (
                        <p className="cn-legal-error" role="alert">
                          {checkoutError}
                        </p>
                      )}
                      <LegalLinks />

                      <div className="lp6-price-meta">
                        <span>
                          <FontAwesomeIcon icon={faUsers} />{" "}
                          {t("lp6.priceSeats")}
                        </span>
                        <span>
                          <FontAwesomeIcon icon={faClock} />{" "}
                          {t("lp6.priceStart")}
                        </span>
                      </div>
                    </>
                  ) : (
                    <>
                      <div className="lp6-price-btn lp6-price-soon" aria-disabled="true">
                        <FontAwesomeIcon icon={faClock} />{" "}
                        {t("enrollment.comingSoonCta")}
                      </div>

                      <p className="lp6-price-soon-note">
                        {t("enrollment.comingSoonNote")}
                      </p>
                    </>
                  )}

                  <div className="lp6-price-guarantee">
                    <FontAwesomeIcon icon={faShieldHalved} />
                    <a className="cn-legal-inline-link" href={LEGAL_PATHS.refunds}>
                      {t("lp6.guaranteeTitle")}
                    </a>
                  </div>
                </div>
              </Col>
            </Row>
          </Container>
        </section>

        {/* --------------------------- GUARANTEE -------------------------- */}
        <section className="lp6-guarantee">
          <Container>
            <div className="lp6-guarantee-card" data-reveal="">
              <div className="lp6-guarantee-badge">
                <FontAwesomeIcon icon={faShieldHalved} />
              </div>
              <div>
                <span className="lp6-eyebrow-2">
                  {t("lp6.guaranteeEyebrow")}
                </span>
                <h3>{t("lp6.guaranteeTitle")}</h3>
                <p>{t("lp6.guaranteeBody")}</p>
                <p>
                  <a className="cn-legal-inline-link" href={LEGAL_PATHS.refunds}>
                    {t("lp6.guaranteeLink")}
                  </a>
                </p>
              </div>
            </div>
          </Container>
        </section>
      </main>
      <MainFooter />
    </>
  );
};

export default Subscriptions3;
